import { Skeleton, Stack, VStack } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { FeatureFlag } from '@revelio/core';

import type { TNode } from '../../../../../shared/components/tree/Node';
import { AllCompaniesPopoutTree } from '../../../../../shared/components/tree/PopoutTree/Async/AllCompaniesPopoutTree';
import { SelectedValuesDisplayComponent } from '../../../../../shared/components/tree/PopoutTree/Async/SelectedValueDisplay';
import { PopoutTree } from '../../../../../shared/components/tree/PopoutTree/PopoutTree';
import { useTreeSelections } from '../../../../../shared/components/tree/use-tree-selections';
import type { TreeApi } from '../../../../../shared/components/tree/useTreeApi';
import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useCustomSkillLevelLabels } from '../../../../../shared/selection-lists/hooks/useCustomSkillLevelLabels';
import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../components/PageConfigProvider/types';
import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import {
  type TreeData,
  useSingleFilterData,
} from '../../contexts/FilterProvider';
import {
  FilterId,
  type TreeFilter,
  type TreeSelection,
} from '../../types/filters';
import { getNumericIdFromNestedId } from '../../utils/get-numeric-id-from-nested-id';
import { isRicsReferenceFilter } from '../../utils/ricsTaxonomyTree';
import { ToggleSelect } from '../ToggleSelect';
import { GeographyModeBranchLabel } from './GeographyModeSelect';
import { NotToggle } from './NotToggle';

const findTreeDataById = (
  items: TreeData[],
  idToFind: string
): TreeData | undefined => {
  for (const item of items) {
    if (item.id === idToFind) {
      return item;
    }
    if (item.children) {
      const found = findTreeDataById(item.children, idToFind);
      if (found !== undefined) {
        return found;
      }
    }
  }
  return undefined;
};

interface TreeFilterFormProps {
  filter: TreeFilter;
  closeMenu: () => void;
  disallowMultiLevelSelect: boolean;
  isOpen?: boolean;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  isSelfSignupUser?: boolean;
  selectionChange?: () => void;
  onSave: (filter: TreeFilter) => void;
  isNewInstance?: boolean;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
  disabledLevels?: number[];
  maxSelections?: number;
  showSelectedValues?: boolean;
  currentTreeSelections?: TreeSelection[];
  replaceSingleSelections?: boolean;
}

export const TreeFilterForm: React.FC<TreeFilterFormProps> = ({
  filter,
  closeMenu,
  isOpen,
  disallowMultiLevelSelect,
  searchInputRef,
  isSelfSignupUser,
  selectionChange,
  onSave,
  isNewInstance,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
  disabledLevels: disabledLevelsProp,
  maxSelections,
  showSelectedValues = false,
  currentTreeSelections,
  replaceSingleSelections = false,
}) => {
  const { revelioPageId } = useTabContentConfig();
  const location = useLocation();
  const isSelectionListIdsEnabled = useUnleashFlag(
    FeatureFlag.SelectionListIds
  );
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);
  const geoUserPref = useUnleashFlag(FeatureFlag.GeographyUserPreference);
  const discoveryStateGeo = useUnleashFlag(FeatureFlag.DiscoveryStateGeography);
  const tdStateGeo = useUnleashFlag(FeatureFlag.TalentDiscoveryStateGeography);
  const isGeographyPreferenceEnabled =
    geoUserPref || discoveryStateGeo || tdStateGeo;
  const isRicsTaxonomyTreeEnabled =
    useReferenceApi && isRicsReferenceFilter(filter.id);

  const data = useSingleFilterData(filter.id);
  const treeData = (() => {
    return data as TreeData[] | null;
  })();

  // Fetch labels for custom skill taxonomy directly from API (no per-node duplication)
  const { labelsByDepth } = useCustomSkillLevelLabels(0);

  // Create dynamic branches function for custom skill filter
  const dynamicBranches = useMemo(() => {
    if (filter.id !== FilterId.CUSTOM_SKILL) return filter.branches;
    return (parentNode: TNode) => {
      const childrenDepth = parentNode.level + 1;
      const label = labelsByDepth?.[childrenDepth];
      return { label: label || `Level ${childrenDepth}` };
    };
  }, [filter.branches, labelsByDepth, filter.id]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  useEffect(() => {
    if (treeData) {
      setHasFetched(true);
    }
  }, [treeData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const baseIds =
      (filter.isPrimary ? filter.primarySelections : filter.selections)?.map(
        (s) => s.id
      ) || [];
    const idsToShow = (() => {
      if (!treeData?.length || baseIds.length === 0) {
        return baseIds;
      }
      const flattenIds = (nodes: TreeData[]): string[] =>
        nodes.flatMap((n) => [
          n.id,
          ...(n.children ? flattenIds(n.children) : []),
        ]);
      const allNodeIds = new Set(flattenIds(treeData));
      const selectionListIds = filter.selectionListIds ?? [];
      const idToLevelId = new Map(
        (filter.selections ?? [])
          .filter(
            (s): s is TreeSelection => s?.id != null && s?.levelId != null
          )
          .map((s) => [String(s.id), s.levelId])
      );

      const resolved = baseIds.flatMap((id) => {
        if (allNodeIds.has(id)) return [id];
        const levelId = idToLevelId.get(id);
        const depth =
          levelId !== undefined ? selectionListIds.indexOf(levelId) : -1;

        const suffixMatch = Array.from(allNodeIds).filter(
          (nodeId) => nodeId === id || nodeId.endsWith('_' + id)
        );
        const depthFiltered =
          depth >= 0 && suffixMatch.length > 1
            ? suffixMatch.filter(
                (nodeId) => nodeId.split('_').length === depth + 1
              )
            : suffixMatch;
        if (depthFiltered.length > 0) return depthFiltered;

        const numericId = getNumericIdFromNestedId(id);
        if (numericId !== null && levelId !== undefined && depth >= 0) {
          const match = Array.from(allNodeIds).find((nodeId) => {
            const nodeDepth = nodeId.split('_').length - 1;
            const nodeNumeric = getNumericIdFromNestedId(nodeId);
            return nodeDepth === depth && nodeNumeric === numericId;
          });
          if (match !== undefined) return [match];
        }

        return [id];
      });
      return [...new Set(resolved)];
    })();
    if (!isEqual(selectedIds, idsToShow)) {
      setSelectedIds(idsToShow);
    }
  }, [
    filter.id,
    filter.selections,
    filter.primarySelections,
    filter.selectionListIds,
    filter.isPrimary,
    treeData,
    selectedIds,
  ]);

  const handleApply = useCallback(
    (treeSelectedIds: string[], selectedNodes?: TNode[]) => {
      if (!filter || !treeSelectedIds.length) return;

      // Create a map of existing selection IDs to names to preserve user-friendly
      // labels when tree item data is unavailable
      const filterIdToNameMap = new Map(
        (filter.selections || [])
          .filter((s) => s?.id && s?.name)
          .map((s) => [s.id, s.name])
      );

      const newSelections: TreeSelection[] = treeSelectedIds
        .map((id): TreeSelection | null => {
          if (!id || typeof id !== 'string') return null;

          const selectedNode = selectedNodes?.find((n) => n.id === id);
          const treeItem =
            selectedNode?.data || findTreeDataById(treeData || [], id);

          const parts = id.split('_');
          const depth = Math.max(0, parts.length - 1);
          const levelId = filter.selectionListIds?.[depth];
          if (!levelId) return null;

          const existingName = filterIdToNameMap.get(id);
          const name = treeItem?.name || existingName || id;

          return {
            id,
            name,
            levelId,
            isCurrent,
          };
        })
        .filter((s): s is TreeSelection => s !== null);

      const updatedFilter: TreeFilter = {
        ...filter,
        selections: newSelections,
        isCurrent,
        isNot: isNotEnabled,
      };

      onSave(updatedFilter);
      selectionChange?.();
      closeMenu();
    },
    [
      filter,
      closeMenu,
      treeData,
      isCurrent,
      isNotEnabled,
      onSave,
      selectionChange,
    ]
  );

  // showToggle: whether to show the Current/Previous toggle UI
  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  // Use tree selections hook for manual control (always call hooks)
  const {
    selections,
    setSelections,
    onChange,
    onClear,
    validateMinSelections,
  } = useTreeSelections({
    initialSelections: selectedIds,
    disallowMultiLevelSelect,
    maxSelections: maxSelections ?? 12,
    minSelections: 0,
    filterLabel: filter.name,
  });

  const popoutTreeRef = useRef<TreeApi>();

  useEffect(() => {
    setSelections(selectedIds);
  }, [selectedIds, setSelections]);

  useEffect(() => {
    const treeApi = popoutTreeRef.current;
    if (isOpen && treeApi) {
      treeApi.onSearch('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!validateMinSelections()) {
      return;
    }
    handleApply(selections);
  };

  const selectionRecordsForSelectedItems =
    (filter.isPrimary ? filter.primarySelections : filter.selections) ?? [];
  const shouldSortSuggestedSearch =
    filter.id === FilterId.ROLE ||
    filter.id === FilterId.GEOGRAPHY ||
    isRicsTaxonomyTreeEnabled;

  if (
    (filter.id === FilterId.COMPANY ||
      filter.id === FilterId.COMPETITORS ||
      filter.id === FilterId.FOUNDER_COMPANY) &&
    treeData
  ) {
    return (
      <AllCompaniesPopoutTree
        searchInputRef={searchInputRef}
        treeData={treeData}
        activeIds={selectedIds}
        activeNodeData={currentTreeSelections?.map((s) => ({
          id: s.id,
          name: s.name,
          levelId: s.levelId,
          isCurrent: s.isCurrent,
        }))}
        filterLabel={filter.name}
        branches={dynamicBranches}
        sortSuggestedSearch={shouldSortSuggestedSearch}
        onSubmit={handleApply}
        onClose={closeMenu}
        isOpen={isOpen}
        disabledLevels={
          disabledLevelsProp ??
          (revelioPageId === RevelioPageId.COMPANY_OVERVIEW_V2 ||
          revelioPageId === RevelioPageId.COMPANY_SCREENER_V2 ||
          filter.id === FilterId.COMPETITORS ||
          filter.id === FilterId.FOUNDER_COMPANY
            ? [0, 1]
            : undefined)
        }
        maxSelections={maxSelections ?? 12}
        disallowMultiLevelSelect={disallowMultiLevelSelect}
        isSelfSignupUser={isSelfSignupUser}
        selectionChange={selectionChange}
        showSelectedValues={showSelectedValues}
        replaceSingleSelections={replaceSingleSelections}
        isSelectionListIdsEnabled={isSelectionListIdsEnabled}
        middleContent={
          showNotToggle && onIsNotEnabledChange ? (
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
              pl={0}
            />
          ) : undefined
        }
        bottomContent={
          showToggle ? (
            <ToggleSelect
              filterName={filter.name}
              onChange={(e: number) => setIsCurrent(e === 0)}
              initialIsCurrent={isCurrent}
            />
          ) : undefined
        }
      />
    );
  }

  if (!treeData && !hasFetched) {
    return (
      <Stack spacing={2} p={4}>
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
        <Skeleton height="20px" />
      </Stack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {showSelectedValues && selections.length > 0 && (
        <SelectedValuesDisplayComponent
          selections={selections.map((id) => {
            const sel = selectionRecordsForSelectedItems.find(
              (s) => s.id === id
            );
            const treeItem = findTreeDataById(treeData || [], id);
            return { id, name: sel?.name ?? treeItem?.name ?? id, depth: 0 };
          })}
          onRemove={(selectionId) => {
            setSelections(selections.filter((id) => id !== selectionId));
          }}
        />
      )}
      <VStack spacing={2} align="stretch">
        <PopoutTree
          data={treeData || []}
          value={selections}
          onChange={onChange}
          branches={dynamicBranches}
          ref={popoutTreeRef}
          sortSuggestedSearch={shouldSortSuggestedSearch}
          disabledLevels={filter.disabledLevels}
          searchInputRef={searchInputRef}
          isSelectionListIdsEnabled={isSelectionListIdsEnabled}
          renderBranchLabel={
            filter.id === FilterId.GEOGRAPHY && isGeographyPreferenceEnabled
              ? (_branch, drillIndex) =>
                  drillIndex === 2 ? <GeographyModeBranchLabel /> : undefined
              : undefined
          }
          middleContent={
            showNotToggle && onIsNotEnabledChange ? (
              <NotToggle
                filterName={filter.name}
                isNot={isNotEnabled}
                onChange={onIsNotEnabledChange}
                pl={0}
              />
            ) : undefined
          }
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </VStack>
      <SelectionListControls
        onClear={onClear}
        onClose={closeMenu}
        onSubmit={handleSubmit}
        isSubmitDisabled={!selections.length}
        submitText={
          isNewInstance || selectedIds.length === 0 ? 'Add' : 'Update'
        }
      />
    </VStack>
  );
};
